import qs from "qs";

import migrateAnalysisSortValue from "./migrateAnalysisSortValue";

type Sort = string;

type FilteredCategoryIds = string[];

export type DisplayStyle = "grid" | "list";

const ALLOWED_DISPLAY_STYLES = ["grid", "list"];

class AnalysisPersistenceLayer {
  _defaultStorageKeyName = "analysisPersistenceLayer";
  _defaultStorageDisplayStyleKeyName = "analysisDisplayStyle";

  /**
   *
   * @param storageProvider - Use localStorage or any storageProvider with implements functions localStorage like
   * @param keyPrefix
   */
  constructor(storageProvider, keyPrefix = "") {
    this.storageProvider = storageProvider;

    this._storageKeyName = keyPrefix
      ? `${keyPrefix}_${this._defaultStorageKeyName}`
      : this._defaultStorageKeyName;

    this._storageDisplayStyleKeyName = keyPrefix
      ? `${keyPrefix}_${this._defaultStorageDisplayStyleKeyName}`
      : this._defaultStorageDisplayStyleKeyName;
  }

  setSortFilter = async (value: Sort) => {
    return await this.appendFilters({ sort: value });
  };

  setFilteredCategories = async (filteredCategoryIds: FilteredCategoryIds) => {
    return await this.appendFilters({ filteredCategoryIds });
  };

  setDisplayStyle = async (style: DisplayStyle) => {
    if (ALLOWED_DISPLAY_STYLES.includes(style)) {
      await this.storageProvider.setItem(
        this._storageDisplayStyleKeyName,
        style
      );

      return style;
    }
  };

  getDisplayStyle = async () => {
    const style = await this.storageProvider.getItem(
      this._storageDisplayStyleKeyName
    );

    if (ALLOWED_DISPLAY_STYLES.includes(style)) {
      return style;
    }
  };

  getFilters = async (): Promise<{
    sort: Sort | undefined,
    filteredCategoryIds: FilteredCategoryIds | undefined
  }> => {
    const result = {};

    try {
      const data = JSON.parse(
        await this.storageProvider.getItem(this._storageKeyName)
      );

      if (data?.sort) {
        const migratedSort = migrateAnalysisSortValue(data.sort);
        result.sort = migratedSort;

        if (migratedSort !== data.sort) {
          await this.save({ ...data, sort: migratedSort });
        }
      }

      if (data?.filteredCategoryIds?.filter(Boolean).length > 0) {
        result.filteredCategoryIds = data.filteredCategoryIds;
      }
    } catch (e) {
      console.error(e);
    }

    return result;
  };

  toString = async () => {
    const { filteredCategoryIds, sort } = await this.getFilters();

    return qs.stringify(
      { ordering: sort, category: filteredCategoryIds },
      { arrayFormat: "repeat" }
    );
  };

  purgeString = string => {
    if (typeof string !== "string") {
      return;
    }

    const params = qs.parse(string, {
      ignoreQueryPrefix: true
    });

    delete params?.ordering;
    delete params?.category;

    return qs.stringify(params, { arrayFormat: "repeat" });
  };

  restoreFromString = async (value: string) => {
    try {
      const params = qs.parse(value);

      const restoredFilters = {};

      if (typeof params?.ordering === "string") {
        restoredFilters.sort = migrateAnalysisSortValue(params.ordering);
      }

      if (params.category) {
        const categoryIds = Array.isArray(params.category)
          ? params.category
          : [params.category];

        let filteredCategoryIds = [];

        categoryIds.forEach(category => {
          let normalizedCategory = null;

          if (typeof category === "number" && !isNaN(category)) {
            normalizedCategory = category;
          } else if (typeof category === "string" && category.trim()) {
            normalizedCategory = !isNaN(category) ? Number(category) : category;
          }

          if (normalizedCategory !== null) {
            filteredCategoryIds.push(normalizedCategory);
          }
        });

        if (filteredCategoryIds.length > 0) {
          restoredFilters.filteredCategoryIds = filteredCategoryIds;
        }
      }

      if (Object.keys(restoredFilters).length > 0) {
        return await this.save(restoredFilters);
      }
    } catch (e) {
      console.error(e);
    }
  };

  save = async value => {
    await this.storageProvider.setItem(
      this._storageKeyName,
      JSON.stringify(value)
    );

    return value;
  };

  appendFilters = async value => {
    try {
      const data = await this.getFilters();
      const newFilters = { ...data, ...value };

      return await this.save(newFilters);
    } catch (e) {
      console.error(e);
    }
  };
}

export default AnalysisPersistenceLayer;
